import { useMemo, useState } from "react";
import Cookies from "js-cookie";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table/table";
import { Badge } from "@/components/ui/badge/badge";
import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card/hover-card";
import { BreakList } from "@/components/break/break-list";
import { formatDuration } from "@/components/break/format";
import { formatTimeToIST } from "@/utils/timezone";
import { getTeamDayQueryOptions } from "@/config/queries/teamleader/tl-query-options";

/** yyyy-mm-dd for the IST day, so the picker matches what the API considers "today". */
function istDayString(d = new Date()): string {
  return new Date(d.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
}

// "Not checked in" must mean exactly that. Someone who worked a full day and
// checked out is *not* the same as someone who never arrived.
const statusVariant = (row: { onBreak: boolean; checkedIn: boolean; checkinAt: string | null; checkoutAt: string | null }) =>
  row.onBreak
    ? { label: "On a break", className: "bg-amber-500 text-black hover:bg-amber-500" }
    : row.checkedIn
    ? { label: "Working", className: "bg-green-600 hover:bg-green-600" }
    : row.checkoutAt || row.checkinAt
    ? { label: "Checked out", variant: "outline" as const }
    : { label: "Not checked in", variant: "secondary" as const };

export default function TeamLeaderTodayPage() {
  const key = Cookies.get("key") as string;
  const [day, setDay] = useState(istDayString());

  // Parse the picked IST day back to an instant inside that day.
  const date = useMemo(() => new Date(`${day}T12:00:00+05:30`), [day]);
  const { data, isFetching, refetch } = useQuery(getTeamDayQueryOptions(key, date));

  const rows = data?.employees ?? [];
  const totals = useMemo(
    () => ({
      onBreak: rows.filter((r) => r.onBreak).length,
      present: rows.filter((r) => r.checkinAt).length,
      working: rows.filter((r) => r.checkedIn).length,
      breakSeconds: rows.reduce((s, r) => s + r.totalBreakSeconds, 0),
      breakCount: rows.reduce((s, r) => s + r.breakCount, 0),
    }),
    [rows]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium">Team — day view</h1>
          <p className="text-sm text-muted-foreground">
            {rows.length} employees · {totals.present} present · {totals.working} still working ·{" "}
            {totals.onBreak} on a break · {totals.breakCount} breaks totalling{" "}
            {formatDuration(totals.breakSeconds)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={day}
            max={istDayString()}
            onChange={(e) => setDay(e.target.value)}
            className="w-[170px]"
            data-testid="tl-date"
          />
          <Button
            size="sm"
            variant="outline"
            onClick={() => refetch()}
            className="gap-2"
            data-testid="tl-refresh"
          >
            <RefreshCw className={isFetching ? "w-4 h-4 animate-spin" : "w-4 h-4"} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-md border bg-card overflow-x-auto">
        <Table data-testid="tl-day-table">
          <TableHeader>
            <TableRow>
              <TableHead>Employee</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Checked in</TableHead>
              <TableHead>Checked out</TableHead>
              <TableHead>Breaks</TableHead>
              <TableHead>Total break</TableHead>
              <TableHead>Attendance</TableHead>
              <TableHead>Leave</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  {isFetching ? "Loading…" : "No employees in scope for this day."}
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => {
              const s = statusVariant(r);
              return (
                <TableRow key={r.employee._id} data-testid="tl-row">
                  <TableCell className="font-medium">{r.employee.name}</TableCell>
                  <TableCell>
                    <Badge variant={s.variant} className={s.className}>
                      {s.label}
                    </Badge>
                  </TableCell>
                  <TableCell>{r.checkinAt ? formatTimeToIST(r.checkinAt) : "—"}</TableCell>
                  <TableCell>{r.checkoutAt ? formatTimeToIST(r.checkoutAt) : "—"}</TableCell>
                  <TableCell>
                    {r.breakCount === 0 ? (
                      <span className="text-muted-foreground">—</span>
                    ) : (
                      <HoverCard>
                        <HoverCardTrigger asChild>
                          <button
                            type="button"
                            className="underline decoration-dotted underline-offset-4"
                            data-testid="tl-breaks-cell"
                          >
                            {r.breakCount}
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80">
                          <BreakList breaks={r.breaks} />
                        </HoverCardContent>
                      </HoverCard>
                    )}
                  </TableCell>
                  <TableCell className="font-medium" data-testid="tl-total-break">
                    {formatDuration(r.totalBreakSeconds)}
                  </TableCell>
                  <TableCell>
                    {r.attendance ? (
                      <span>
                        {r.attendance.status}
                        {r.attendance.hoursWorked != null && (
                          <span className="text-muted-foreground"> · {r.attendance.hoursWorked}h</span>
                        )}
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {r.leave ? (
                      <span className="text-sm">
                        {r.leave.leaveType} <span className="text-muted-foreground">({r.leave.status})</span>
                      </span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
